import React, { useEffect, useState, useRef } from "react";
import CustomTable from "../../components/Table";
import Header from "../../components/Header";
import AdminCointainer from "../../containers/Admin";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/AddButton";
import { client, Plus, vehicles } from "../../common/svg";
import { ApiService } from "../../service/api.service";
import { Column, SurveyorData } from "../../service/interfaces/Table.interface";
import moment from "moment";
import { MASTER_URL } from "../../common/common";
import Managesurveyor from "../../containers/Forms/Surveyor/ManageSurveyor";
import { set } from "react-hook-form";

const Surveyor = () => {
  const navigate = useNavigate();
  const apiService = new ApiService();
  const [clientData, setClientData] = useState<SurveyorData[]>([]);
  const [search, setSearch] = useState<string>("");
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false); // State for modal
  const entityId = localStorage.getItem('entity_id');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const columns: Column<SurveyorData>[] = [
    { header: "Surveyor Name", dataKey: "name" },
    { header: "Contact Number", dataKey: "contactNumber" },
    { header: "Location", dataKey: "location" },
    {
      header: "Actions",
      dataKey: "surveyorId",
      render: (value, row) => (
        <div className="relative" ref={activeMenu === value ? menuRef : null}>
          <button
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
            onClick={(e) => {
              e.stopPropagation();
              setActiveMenu(activeMenu === value ? null : String(value));
            }}
          >
            <div className="flex flex-col items-center justify-center h-5">
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full mb-0.5"></div>
              <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            </div>
          </button>

          {activeMenu === value && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 py-1 border border-gray-200">
              <button
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row);
                  setActiveMenu(null);
                }}
              >
                Edit
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  const handleEdit = (item: SurveyorData) => {
    setIsModalOpen(true);
    setSelectedId(item.surveyorId);
    // navigate(`managesurveyor/${item.surveyorId}`);
  };

  const handleDelete = (item: SurveyorData) => {
    if (window.confirm(`Are you sure you want to delete ${item.name}?`)) {
      deleteClient(item.surveyorId);
    }
  };

  const deleteClient = async (clientId: string) => {
    try {
      const response: any = await apiService.deleteAPi(`${MASTER_URL}/surveyor/delete-route/${clientId}`);
      if (response.status === "200") {
        getClientData();
      }
    } catch (error) {
      console.error("Error deleting surveyor:", error);
    }
  };

  const getClientData = async (isLoadingMore = false) => {
    if (!hasMoreData && isLoadingMore) return;

    setLoading(true);
    try {
      const response: any = await apiService.getApi(
        `${MASTER_URL}/surveyor/get-route?page=${currentPage}&limit=${pageSize}&search=${search}&entityId=${entityId}`
      );

      if (response.status === "200") {
        const formattedData = response.body.data.map((client: any) => ({
          name: client.firstName + " " + (client.lastName || ""),
          contactNumber: client.contactNumber || "N/A",
          location: client.location || "N/A",
          surveyorId: client.surveyorId || "N/A",
        }));

        const totalRecords = parseInt(response.body.pagination?.totalRecords) || 0;
        const currentLoadedItems = isLoadingMore
          ? clientData.length + formattedData.length
          : formattedData.length;

        setHasMoreData(currentLoadedItems < totalRecords);

        setClientData(prevData =>
          isLoadingMore ? [...prevData, ...formattedData] : formattedData
        );

        setTotalItems(totalRecords);
      }
    } catch (error) {
      console.error("Error fetching client data:", error);
      setHasMoreData(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (hasMoreData) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getClientData(true);
    }
  };

  useEffect(() => {
    setHasMoreData(true);
    getClientData();
  }, [search, entityId]);

  const handleRowClick = (item: SurveyorData) => {
    // Handle row click if needed
  };

  // Updated handleAdd function to open modal
  const handleAdd = () => {
    setIsModalOpen(true);
  };

  // Function to handle modal close and refresh data
  const handleModalClose = (shouldRefresh = false) => {
    setIsModalOpen(false);
    if (shouldRefresh) {
      getClientData(); // Refresh the table data
    }
  };

  return (
    <div className="">
      <Header icon={client} title="Surveyor" showSearch={true} setSearch={setSearch}>
        <div>
          <CustomButton label="" onClick={handleAdd} icon={Plus} />
        </div>
      </Header>

      <AdminCointainer>
        <CustomTable<SurveyorData>
          columns={columns}
          data={clientData}
          onRowClick={handleRowClick}
          loading={loading}
          onLoadMore={handleLoadMore}
        />
      </AdminCointainer>

      {/* Modal Component */}
      <Managesurveyor
        open={isModalOpen}
        setOpen={setIsModalOpen}
        id={selectedId}
        onSuccess={() => getClientData()}
      />
    </div>
  );
};

export default Surveyor;